import { useEffect, useRef, useCallback } from 'react';
import { ExtrasModel } from '../model/extras.model';
import { getPending, toUpdateContentInput } from './extras.mappers';
import {
  useVideoStatusModel,
  VideoById,
  VideoStatus,
} from '@client/new/common/videos/useUpload';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';
import { Content, ContentVideo } from '../model/extras.domain';

export const useProcessPendingElements = (model: ExtrasModel) => {
  const influencerId = useInfluencerId();
  const { getVideo, startPollingForVideo } = useVideoStatusModel({
    creatorId: influencerId,
  });

  const processedVideoIds = useRef<Set<string>>(new Set());

  const updateVideoContent = useCallback(
    async (contentId: string, content: ContentVideo['value']) => {
      try {
        await model.update(
          contentId,
          toUpdateContentInput({
            type: 'ContentVideo',
            value: { ...content, isProcessed: true },
          } as Content),
        );
      } catch (error) {
        console.error('Failed to update content:', error);
      }
    },
    [model],
  );

  const handleVideoProcessingComplete = useCallback(
    (contentId: string, content: ContentVideo['value']) => {
      return (video: VideoById) => {
        if (video?.status === VideoStatus.ProcessingFinished) {
          updateVideoContent(contentId, content);
        }
      };
    },
    [updateVideoContent],
  );

  useEffect(() => {
    if (model.data.type !== 'fetched') return;

    const pending = getPending(model.data.value.contentMap);
    if (!pending.length) return;

    pending.forEach((content) => {
      if (processedVideoIds.current.has(content.libraryVideoId)) return;

      const cachedVideo = getVideo(content.libraryVideoId);

      if (cachedVideo?.status === VideoStatus.ProcessingFinished) {
        updateVideoContent(content.id, content);
      } else {
        startPollingForVideo(
          content.libraryVideoId,
          handleVideoProcessingComplete(content.id, content),
        );
      }

      processedVideoIds.current.add(content.libraryVideoId);
    });
  }, [model.data, updateVideoContent, handleVideoProcessingComplete]);

  useEffect(() => {
    if (model.data.type !== 'fetched') return;

    const currentPendingIds = new Set(
      getPending(model.data.value.contentMap).map((p) => p.libraryVideoId),
    );

    processedVideoIds.current = new Set(
      Array.from(processedVideoIds.current).filter((id) =>
        currentPendingIds.has(id),
      ),
    );
  }, [model.data.type === 'fetched' ? model.data.value.contentMap : null]);
};
